import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { X } from 'lucide-react';
import { EmailSheet } from './EmailSheet';
import { FolderSheet } from './FolderSheet';
import { ScanSheet } from './ScanSheet';
import { currentScanSession, onScanStopped } from './scan-session';
import type { PaigeIntakeKind } from './paige.global';

export interface IntakeSheetProps {
  kind: PaigeIntakeKind;
  onClose: () => void;
}

/**
 * What "Add pages" opens in the browser for the sources that are not a file picker: the scanner
 * (Paige Scan on this computer, driven from here), the job's email address and its watched folder. A sheet over the
 * workbench in CapturePoint's own surface language. Closing the scanner sheet settles the scan the
 * screen is waiting on as cancelled.
 */
export function IntakeSheet({ kind, onClose }: IntakeSheetProps): ReactNode {
  const close = () => {
    if (kind === 'scan') currentScanSession()?.cancel();
    onClose();
  };
  const backdrop = useInertBehind();
  // Stop on the screen behind the sheet ends the scan; the sheet goes with it.
  useEffect(() => (kind === 'scan' ? onScanStopped(onClose) : undefined), [kind, onClose]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  return (
    <div ref={backdrop} className="paige-intake-backdrop" onClick={close}>
      <section
        className={`paige-intake-sheet${kind === 'email' ? ' is-email' : kind === 'scan' ? ' is-scan' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="paige-intake-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="paige-intake-close" onClick={close} aria-label="Close">
          <X size={16} />
        </button>
        {kind === 'scan' ? (
          <ScanSheet onAdded={onClose} />
        ) : kind === 'folder' ? (
          <FolderSheet />
        ) : (
          <EmailSheet />
        )}
      </section>
    </div>
  );
}

/**
 * A modal sheet: the screen behind it takes no pointer and no focus while it is open, and the
 * review screen's own shortcuts stand down behind anything inert (CapturePoint's Stop buttons sit
 * there for as long as a scan sheet is up). The ref goes on the sheet's backdrop.
 */
function useInertBehind(): RefObject<HTMLDivElement | null> {
  const backdrop = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const behind = [...(backdrop.current?.parentElement?.children ?? [])].filter(
      (element): element is HTMLElement =>
        element instanceof HTMLElement && element !== backdrop.current && !element.inert,
    );
    for (const element of behind) element.inert = true;
    return () => {
      for (const element of behind) element.inert = false;
    };
  }, []);
  return backdrop;
}
